import { HotelStars } from '@lt/components';
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';

import Wishlist from 'src/components/Wishlist';
import PriceBadgeContainer from '../../containers/PriceBadge';
import Breadcrumbs from '../Breadcrumbs';
import LabelList from '../LabelList';
import ShareSocial from '../ShareSocial';
import ShortInfo from './ShortInfo';

const Header = ({
  title,
  stars,
  breadcrumbs,
  subheader,
  isMobile,
  badgeList,
  isHotelOnly,
  isWishlistAvailable,
  hotelId,
}) => (
  <StyledContainer isMobile={isMobile}>
    <StyledStarsLocation isMobile={isMobile}>
      <StyledHotelStars value={stars} />
      {breadcrumbs && (
        <StyledBreadcrumbs
          items={breadcrumbs}
          smartFade={!isMobile}
          fullWidth={isMobile}
          isMobile={isMobile}
        />
      )}
      {!isMobile && <StyledShareSocialDesktop description={subheader} />}
      {isMobile && <Wishlist wishlistIdentifier={hotelId} type="hotel" />}
    </StyledStarsLocation>
    <StyledRow>
      <StyledHeaderContent isMobile={isMobile}>
        <StyledTitle>
          <StyledH1 isMobile={isMobile}>
            <StyledTitleBold>{title}</StyledTitleBold>
          </StyledH1>
          {!isMobile && <PriceBadgeContainer isHotelOnly={isHotelOnly} />}
        </StyledTitle>

        <SubheaderWrapper isMobile={isMobile}>
          <LabelList badges={badgeList} />

          {subheader && <ShortInfo text={subheader} isMobile={isMobile} />}
        </SubheaderWrapper>
      </StyledHeaderContent>
      {!isMobile && isWishlistAvailable && (
        <Wishlist type="hotel" wishlistIdentifier={hotelId} isHotelPage />
      )}
    </StyledRow>
    {isMobile && (
      <StyledShareSocialMobile stickTo="left" description={subheader} />
    )}
  </StyledContainer>
);

const StyledContainer = styled.div`
  background: ${({ theme }) => theme.colors.white};

  ${({ isMobile }) =>
    isMobile
      ? `
    padding: 8px 0 15px 0;
  `
      : `
    padding: 10px 5px 20px;
  `}
`;

const SubheaderWrapper = styled.div`
  display: flex;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column-reverse' : 'column')};
`;

const StyledRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

const StyledHeaderContent = styled.div`
  margin-right: ${({ isMobile }) => (isMobile ? 0 : 20)}px;
  max-width: ${({ isMobile }) => (isMobile ? 100 : 90)}%;
`;

const StyledStarsLocation = styled.div`
  margin-bottom: 3px;
  display: flex;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.blackLight};

  ${({ isMobile }) =>
    isMobile &&
    `
    margin-bottom: 5px;
    font-size: 11px;
  `}
`;

const StyledBreadcrumbs = styled(Breadcrumbs)`
  ${({ isMobile }) => isMobile && 'width: 100%;'}
`;

const StyledHotelStars = styled(HotelStars)`
  margin-right: 8px;
`;

const StyledShareSocialDesktop = styled(ShareSocial)`
  margin-left: 15px;
`;

const StyledShareSocialMobile = styled(ShareSocial)`
  margin-top: 10px;
`;

const StyledTitle = styled.div`
  margin-bottom: 3px;
`;

const StyledH1 = styled.h1`
  margin-right: 10px;
  display: inline;
  font-size: 28px;
  line-height: 32px;

  ${({ isMobile }) =>
    isMobile &&
    `
    font-size: 18px;
    line-height: normal;
  `}
`;

const StyledTitleBold = styled.span`
  font-weight: bold;
`;

Header.defaultProps = {
  subheader: null,
  isMobile: false,
  badgeList: null,
};

Header.propTypes = {
  title: PropTypes.string.isRequired,
  stars: PropTypes.number.isRequired,
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      link: PropTypes.string,
      title: PropTypes.string.isRequired,
    }),
  ).isRequired,
  badgeList: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string.isRequired,
    }),
  ),
  subheader: PropTypes.string,
  isMobile: PropTypes.bool,
};

export default Header;
